import styled from 'styled-components';

import { BasicSubtitle, SmallParagraph } from '@genially/design-system';
import { DisplayDownIcon, SmallCrossIcon, SuccessIcon } from '@genially/icons';

import { PANEL_SURFACE } from '../../../common/panelColors';

export const Wrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: size.units(1),
  backgroundColor: PANEL_SURFACE,
  overflow: 'hidden',
}));

export const Header = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: size.units(1.5),
  padding: size.units(1.5),
  width: '100%',
  background: 'none',
  border: 'none',
  textAlign: 'left',
}));

/** Only the rows with something to unfold are buttons; the rest are plain rows. */
export const HeaderButton = styled(Header).attrs({ as: 'button', type: 'button' })({
  cursor: 'pointer',
});

export const Name = styled(BasicSubtitle)(({ theme: { color } }) => ({
  flex: 1,
  color: color.content.reversed.default(),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const Badge = styled(SmallParagraph)(({ theme: { color } }) => ({
  color: color.content.reversed.default(0.7),
  flexShrink: 0,
}));

export const StyledSuccessIcon = styled(SuccessIcon)(({ theme: { color, size } }) => ({
  width: size.units(2),
  height: size.units(2),
  flexShrink: 0,
  color: color.content.success.default(),
}));

export const StyledCrossIcon = styled(SmallCrossIcon)(({ theme: { color, size } }) => ({
  width: size.units(2),
  height: size.units(2),
  flexShrink: 0,
  color: color.content.error.default(),
}));

interface IconContainerProps {
  $type: 'correct' | 'incorrect';
}

export const IconContainer = styled.div<IconContainerProps>(
  ({ theme: { color, size }, $type }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size.units(4),
    height: size.units(3),
    borderRadius: size.units(0.5),

    backgroundColor:
      $type === 'correct'
        ? color.background.complementary(6)
        : color.background.complementary(5),

    [`${StyledSuccessIcon}, ${StyledCrossIcon}`]: {
      color: color.content.secondary.default(),
    },
  }),
);

interface ChevronProps {
  $isExpanded: boolean;
}

export const StyledDisplayDownIcon = styled(DisplayDownIcon)<ChevronProps>(
  ({ theme: { color, size, transition }, $isExpanded }) => ({
    width: size.units(1.5),
    height: size.units(1.5),
    flexShrink: 0,
    color: color.content.primary.disabled(),
    transition: transition.standard('transform'),
    transform: $isExpanded ? 'rotate(180deg)' : 'none',
  }),
);

export const Details = styled.ul(({ theme: { color, size } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: size.half,
  margin: 0,
  padding: `0 ${size.units(1.5)} ${size.units(1.5)} ${size.units(4)}`,
  color: color.content.reversed.default(0.7),
}));

export const Detail = styled.li(({ theme: { color } }) => ({
  wordBreak: 'break-word',
}));

export const DetailItem = styled(SmallParagraph)(({ theme: { color } }) => ({
  color: color.content.primary.disabled(),
}));
